import { NextSeo } from "next-seo";
import Link from "next/link";
import { useRouter } from "next/router";
import { useState } from "react";
import FadeIn from "react-fade-in";
import { Col, Row } from "react-styled-flexboxgrid";
import styled from "styled-components";
import Public from "../Layouts/Public";
import Card from "../components/Card/Card";
import ContactForm from "../components/Form/ContactForm";
import Heading from "../components/Heading";
import Modal from "../components/Modal";
import PageSection, { StyledSectionHeading } from "../components/PageSection";
import Spacer from "../components/Spacer";
import { siteTitle } from "../data/PublicSiteData";
import { trackPageView } from "../includes/analytics";
import { Color } from "../theme/Color";
import { FontSizes } from "../theme/FontSize";

const StyledLink = styled.a`
  color: ${Color.Blue5};
  text-decoration: underline;
  &:hover {
    cursor: pointer;
  }
`;
const StyledFAQTitle = styled.h2`
  font-size: ${FontSizes[3]};
  font-weight: bold;
  margin: 7rem 0 2rem 0;
  text-align: left;
  border-bottom: 2px solid ${Color.Orange5};
`;

const StyledQuestion = styled.h3`
  font-size: ${FontSizes[1]};
  font-weight: bold;
  text-align: left;
  margin: 1rem 0 0.25rem 0;
`;

const StyledAnswer = styled.p`
  margin-bottom: 3rem;
`;

const Faq = () => {
  const [showModal, setShowModal] = useState(false);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const router = useRouter();

  trackPageView("/faq");

  return (
    <>
      <Modal
        data-cy="faq-contact-form"
        modalIsOpen={modalIsOpen}
        afterOpenModal={() => {}}
        setIsOpen={setModalIsOpen}
        title="Need help?"
      >
        <FadeIn>
          <Row center="xs">
            <Col xs={12} md={12} style={{ color: "black" }}>
              <Card hasPadding>
                <Col xs={12}>
                  <Row>
                    <Col xs={12}>
                      <ContactForm />
                    </Col>
                  </Row>
                </Col>
              </Card>
            </Col>
          </Row>
        </FadeIn>
      </Modal>
      <NextSeo
        title={`Frequently Asked Questions | ${siteTitle}`}
        description="Valmeego is a new way for destinations and visitors to interact online.
Get inspiration, local knowledge, advice & deals directly from any destination."
      />
      <PageSection
        backgroundColor={Color.White}
        width="1340px"
        data-cy="core-values"
      >
        <StyledSectionHeading center="xs">
          Frequently Asked Questions
        </StyledSectionHeading>
        <Heading bold={false} centered>
          Need help? If your question is not answered below please{" "}
          <StyledLink onClick={() => setModalIsOpen(true)}>
            contact us
          </StyledLink>
          .
        </Heading>
        <Row center="xs" middle="xs">
          <Col
            xs={10}
            md={8}
            style={{ color: Color.Grey2, marginBottom: "5rem" }}
          >
            <StyledFAQTitle>General</StyledFAQTitle>
            <StyledQuestion>What is Valmeego?</StyledQuestion>
            <StyledAnswer>
              <p>
                Valmeego is a comprehensive platform designed to transform your
                experience of life whether you are at home or travelling.
              </p>
              <p>
                Valmeego is specifically built for the consumer to find and
                directly book anything of interest to them in the entertainment,
                hospitality, travel, and tourism sector.
              </p>
            </StyledAnswer>
            <StyledQuestion>Here's why we're different</StyledQuestion>
            <StyledAnswer>
              <Heading>Local Knowledge</Heading>
              <p>
                Tap into the expertise of locals who are passionate about
                sharing their culture, history, and insights. It's like having a
                personal tour guide in your pocket.
              </p>

              <Heading>Local Expertise</Heading>
              <p>
                Our network of local advisors provides insider knowledge and
                recommendations that go beyond mainstream tourist attractions,
                allowing you to experience a destination like a local.
              </p>
              <Heading>Social Interaction</Heading>
              <p>
                We prioritize social interaction. Connect with other members,
                share insights, and get advice from real people who live in or
                have been to the places you're interested in.
              </p>
              <Heading>Hidden Gems</Heading>
              <p>
                Discover those off-the-beaten-path destinations that aren't
                always in the tourist guides. Find hidden gems and experiences
                that you won't find anywhere else.
              </p>
              <Heading>Personalisation</Heading>
              <p>
                We believe that every traveller is unique. Our platform offers
                personalized trip planning, recommendations, and insights
                tailored to your preferences and interests.
              </p>

              <Heading>Memories</Heading>
              <p>
                Your travel memories matter. Valmeego offers a secure space to
                store all your travel plans, photos, and experiences, allowing
                you to relive your adventures whenever you want.
              </p>
              <p>
                In essence, Valmeego goes beyond the standard technical platform
                by fostering genuine connections, providing local insights, and
                creating a community of like-minded, helpful “locals”.{" "}
              </p>
              <p>
                Valmeego is a hub for building memories and sharing experiences
                with others who share your love of living.
              </p>
              <Heading>Direct Booking and communications:</Heading>
              <p>
                Valmeego provides for direct communications and booking between
                members and businesses. This provides the best value for
                consumers and a better customer relationship for the businesses.
              </p>
              <Heading>All-in-One Solution</Heading>
              <p>
                With Valmeego, you can create, plan, book and manage your
                events, trips, and activities from start to finish, all in one
                place. No need to switch between apps or websites.
              </p>
              <Heading>Easy Group Planning</Heading>
              <p>
                Whether you're traveling with friends, family, or a club, our
                platform simplifies group planning. Coordinate itineraries,
                share recommendations, and keep everyone informed.
              </p>
            </StyledAnswer>
            <StyledQuestion>
              {" "}
              Who can benefit from using Valmeego?{" "}
            </StyledQuestion>
            <StyledAnswer>
              <p>
                Anyone involved in the travel ecosystem, including locals, can
                benefit from using Valmeego.
              </p>
              <ul>
                <li>
                  Locals can connect with fellow community members, access
                  valuable information about events, deals and activities and
                  contribute to the overall growth of their local community.
                </li>
                <li>
                  Local advisors known as Ameegos can monetise their expertise
                  and local knowledge.
                </li>
                <li>
                  Local businesses increase their visibility, direct bookings,
                  and direct connections with existing and potential customers,
                </li>
                <li>
                  Travellers and visitors gain access to personalised
                  recommendations, insider knowledge, trip planning tools, and
                  travel inspiration, enhancing their overall experience.
                </li>
                <li>
                  Destination Promotors increase the visibility of their
                  destination and provide direct connections with existing and
                  potential visitors.
                </li>
              </ul>
            </StyledAnswer>
            <StyledQuestion>What does the name Valmeego mean?</StyledQuestion>
            <StyledAnswer>
              Valmeego derives its name from a combination of 'value' and
              'Amigo'. At its essence Valmeego is striving to create an
              environment where people can connect and co-operate online for the
              benefit of visitors, destinations and the communities who live in
              those destinations. If value is created among people who could
              become friends, then Valmeego will have achieved the hope
              contained in its name.
            </StyledAnswer>
            <StyledQuestion>What is an Ameego?</StyledQuestion>
            <StyledAnswer>
              An Ameego is a person based in the destination with a deep
              knowledge of the destination and / or of a specific activity in
              the destination e.g. skiing, scuba diving etc. They can
              communicate in the local language of the destination and the
              language of the Trip Planner. Alternatively, they may have a
              middle language common to both parties e.g. English. They can
              provide relevant advice and recommendations on accommodation,
              things to see and do, places to eat, how to get about and all the
              little things that make a trip easy and memorable.
              <br />
              <br />
              <p style={{ marginBottom: "1rem" }}>
                There are 3 types of Ameego with different roles:
              </p>
              <ol>
                <li>Local Ameego</li>
                <li>Trip / National Ameego</li>
                <li>Specialist Ameego</li>
              </ol>
            </StyledAnswer>
            <StyledQuestion>
              Can I have a Personal and Ameego profile?
            </StyledQuestion>
            <StyledAnswer>
              Yes. As a member of Valmeego you can create a maximum of 3
              profiles - Personal, Business and Ameego. Many members will only
              ever have a personal account because they do not own a business or
              wish to be an Ameego. However, either or all of these additional
              profiles are available to any Valmeego member if they are
              applicable and they choose to add them.
            </StyledAnswer>
            <StyledQuestion>
              Can I use Valmeego for my Small Tour Operator business?
            </StyledQuestion>
            <StyledAnswer>
              Yes. Use Valmeego to manage each group you have on your tours.
              Create their trip in 3 simple steps and invite them to the trip.
              You can now communicate with the whole group and provide any
              itinerary items, reminders, documents, or other tour related
              information to everyone at the same time. Get paid and manage the
              tour payments through the trip wallet. 
              <Link
                href="https://valmeego-live-resources.s3.eu-west-1.amazonaws.com/pdf/ecosystem-brochures/small-tour-operators.pdf"
                target="_blank"
              >
                Find out more [PDF]
              </Link>
            </StyledAnswer>
            <StyledQuestion>Is the payment system secure?</StyledQuestion>
            <StyledAnswer>
              Yes. We have integrated Stripe as our secure online payments
              processing system. There are some Stripe onboarding requirements
              to ensure security but these are simple and take 5 minutes to
              complete. Payments into and between all wallets on Valmeego take
              seconds and are simple and secure. Alternatively, you can pay for
              anything on Valmeego using your credit card secured through
              Stripe.
            </StyledAnswer>
            <StyledQuestion>Is Valmeego an App?</StyledQuestion>
            <StyledAnswer>
              Valmeego is a Progressive Web Application (PWA), combining the
              convenience of an App and the accessibility of a website. Unlike
              traditional Native Apps, Progressive Web Apps use minimal phone
              memory, storage, and battery life. Popular PWA’s like Uber,
              Pinterest and Spotify have demonstrated the benefits of this
              approach, making Valmeego an efficient and user-friendly choice
              for travellers and locals alike.
            </StyledAnswer>
            <StyledQuestion>How to Add on Android:</StyledQuestion>
            <StyledAnswer>
              <ul>
                <li>Navigate to valmeego.com.</li>
                <li>
                  Click the three-dot overflow menu in the top right corner.
                </li>
                <li>Select “Add to Home Screen”</li>
              </ul>
            </StyledAnswer>
            <StyledQuestion>How to Add on iPhone:</StyledQuestion>
            <StyledAnswer>
              <ul>
                <li>Navigate to Valmeego.com</li>
                <li>Tap the “share” button on the bottom of the screen</li>
                <li>Scroll down and select “Add to Home Screen”</li>
              </ul>
            </StyledAnswer>
            <StyledQuestion>How to Add on Windows:</StyledQuestion>
            <StyledAnswer>
              <ul>
                <li>Google Chrome - navigate to Valmeego.com</li>
                <li>On address bar click “star” icon on the right.</li>
                <li>Click “Done” - Valmeego is now bookmarked.</li>
              </ul>
            </StyledAnswer>

            <StyledFAQTitle>Personal User</StyledFAQTitle>
            <StyledQuestion>Is Valmeego Free?</StyledQuestion>
            <StyledAnswer>
              Yes, Valmeego offers a free membership to Personal users. You can
              explore destinations, connect with fellow travellers, get
              recommendations, and plan your trips without any cost. We believe
              in making travel accessible and enjoyable for everyone. However,
              please keep in mind that there are small transactional charges
              associated with transferring money in or out of the platform.
              These charges help us maintain the secure and seamless financial
              transactions that contribute to a smooth experience for all our
              users.
            </StyledAnswer>
            <StyledQuestion>
              Can I use Valmeego for my club & group trips?
            </StyledQuestion>
            <StyledAnswer>
              Yes. Valmeego is the perfect organisational tool for Club / Group
              trip planners and participants. Valmeego provides a single place
              to plan, organise, book, and pay for all aspects of a trip.
              Research destinations using local expert advisors in your own
              language and with specific knowledge of your club or groups chosen
              activity. Collect money from everyone into the trip wallet, pay
              for booked services from the trip wallet, update itineraries,
              store booking confirmations, store trip photos and have ongoing
              contact among the group through the group chat. 
              <Link
                href="https://valmeego-live-resources.s3.eu-west-1.amazonaws.com/pdf/ecosystem-brochures/clubs.pdf"
                target="_blank"
              >
                Find out more [PDF]
              </Link>
            </StyledAnswer>
            <StyledQuestion>
              Can Valmeego be used for Solo Trips?
            </StyledQuestion>
            <StyledAnswer>
              Yes. Plan a Solo trip with ease. Keep everything in one place for
              all your travels regardless of the purpose or destination of the
              trip. If you are travelling on your own and wish to know where and
              what is safe to do engage with a local Ameego and get the inside
              track. An Ameego also provides the reassurance that you have a
              person to contact in the destination who can help with any
              questions or issues you may have.
            </StyledAnswer>
            <StyledQuestion>
              What does ‘Verified’ mean on an Ameego profile?
            </StyledQuestion>
            <StyledAnswer>
              ‘Verified’ means that an Ameego has provided information to
              Valmeego in order to become active on the system and available for
              selection. This information includes verified photographic
              government issued ID and proof of residence. An Ameego also signs
              up to ‘The 5 Pillars’ upon which they are rated.
            </StyledAnswer>
            <StyledQuestion>What are the 5 Pillars?</StyledQuestion>
            <StyledAnswer>
              {" "}
              <ol>
                <li>
                  Provide impartial advice and recommendations most relevant to
                  the requirements of each Trip
                </li>
                <li>Be respectful, mannerly, courteous, and engaging.</li>
                <li>Be honest, fair, and accurate in your recommendations.</li>
                <li>
                  Be diligent and responsive when providing guidance and
                  recommendations.
                </li>
                <li>
                  Be diligent in keeping your depth of knowledge accurate and up
                  to date.
                </li>
              </ol>
            </StyledAnswer>
            <StyledQuestion>
              Can I ask more than one Ameego to a trip?
            </StyledQuestion>
            <StyledAnswer>
              We propose that it is best if you settle on a single Ameego per
              destination. This allows for a connection and understanding to
              build up between the Ameego and the Trip Planner. After reviewing
              the Ameego profiles you can ask an initial question of a select
              number of Ameego's and make a choice based on the quality of the
              response and answers. Ameego's are rated by previous Trip Planners
              and their profiles provide all the information you will need to
              make your initial choices - language, experience, specialist
              expertise etc.
            </StyledAnswer>
            <StyledQuestion>Do Ameegos charge for their Advice?</StyledQuestion>
            <StyledAnswer>
              Ameegos may charge a small fee for their knowledge and time. Their
              average rate will be communicated on their profile and a rate for
              the level of help you require will be agreed between both parties
              beforehand.
            </StyledAnswer>
            <StyledQuestion>Do I have to tip an Ameego?</StyledQuestion>
            <StyledAnswer>
              No. Giving a gratuity to an Ameego is completely voluntary. We
              propose that the planning of the trip will build a connection
              between the Trip Planner and the Ameego. The Ameego will use all
              their knowledge and experience to provide the best local advice
              and recommendations possible to the Trip Planner. They may even
              provide the benefit of little local connections which a visitor
              would never find themselves. Ameegos will greatly enhance the
              experience and easy organising of the trip. If they have provided
              over and above value to the trip then a fair attitude for their
              help may be to provide a gratuity. That choice is always the Trip
              Planners and their group.
            </StyledAnswer>
            <StyledQuestion>
              Can I ask an Ameego questions outside of a created trip?
            </StyledQuestion>
            <StyledAnswer>
              Yes. An Ameego can be selected or searched for on the Home page -
              Your Travel Feed. They can then be sent a message directly from
              their profile. This is particularly useful when looking for advice
              for day trips or excursions which do not require the creation of a
              trip.
            </StyledAnswer>
            <StyledQuestion>
              Do I have to engage an Ameego when planning a trip?
            </StyledQuestion>
            <StyledAnswer>
              No. Ameegos are a great resource but Trip Planners on Valmeego
              choose if and when to engage an Ameego. An Ameego can be asked to
              provide their expertise at any time or not at all. Many members
              may feel they do not need the help of local knowledge or perhaps
              they are returning to a destination familiar to them. In all
              situations the choice remains with the Trip Planner.
            </StyledAnswer>
            <StyledQuestion>
              Can I still access the information in a trip when it is over?
            </StyledQuestion>
            <StyledAnswer>
              Yes, as long as you maintain your Valmeego membership, which is
              FREE. All the information in the trips you create will be
              accessible whenever you choose to go back into that trip. Great
              for looking back over trips, places you've been and groups you
              travelled with.
            </StyledAnswer>

            <StyledFAQTitle>Ameego</StyledFAQTitle>
            <StyledQuestion>
              Is Valmeego Free to join and work on?
            </StyledQuestion>
            <StyledAnswer>
              Yes. Valmeego is FREE to join as an Ameego. Create your profile,
              complete onboarding with Stripe and agree to the 5 Pillars and you
              are ready to go as a 'Verified' Ameego. This process takes a total
              of 30 minutes to create a solid profile and place yourself in a
              position to earn additional income from your existing knowledge.
              Only 'Verified' Ameego profiles are put live on the system.
            </StyledAnswer>
            <StyledQuestion>Can I be different types of Ameego?</StyledQuestion>
            <StyledAnswer>
              Yes. You can choose to be any or all of the 3 types of Ameego –
              Local / Trip/National / Specialist. Obviously, you will have to
              have the capability to competently fulfil any Ameego role you
              self-identify as. We anticipate that helpful, knowledgeable non-
              commercial local members will gravitate towards the local Ameego
              role while more commercial, tourism industry professionals will
              gravitate towards the Trip and Specialist Ameego roles.
            </StyledAnswer>
            <StyledQuestion>How do I earn revenue as an Ameego?</StyledQuestion>
            <StyledAnswer>
              <p>
                Ameegos may charge a small fee for their knowledge and time.
                Their average rate will be communicated on their profile and a
                rate for the level of help you require will be agreed between
                both parties beforehand.
              </p>
            </StyledAnswer>
            <StyledQuestion>
              Can I, as an Ameego, connect with other Ameego's?
            </StyledQuestion>
            <StyledAnswer>
              Yes. Connect with other Ameego's from all over the world. Enhance
              your own travel experiences and knowledge by connecting with
              like-minded individuals worldwide. Build your own network. Invite
              people you know are suitable as Ameegos to Valmeego. Enjoy the
              interaction of having friends all over the world.
            </StyledAnswer>
            <StyledQuestion>
              Can I invite people I know to be an Ameego or Business on
              Valmeego?
            </StyledQuestion>
            <StyledAnswer>
              Yes. If you know an individual with the knowledge and personality
              to be a solid Ameego you can invite them to join - we provide Sign
              Up links for all user types. If you know a good business and you
              want them to be available on Valmeego for you to recommend then
              you can invite them to join. We provide Sign Up links for each
              user type.
            </StyledAnswer>
            <StyledQuestion>
              How do I get my earnings from Valmeego?
            </StyledQuestion>
            <StyledAnswer>
              By being a 'Verified' Ameego you will have completed the Stripe
              onboarding process which means you can transfer money from the
              Valmeego wallet associated with your Ameego profile into your bank
              account.
            </StyledAnswer>
            <StyledQuestion>
              Will I have my own dashboard on Valmeego?
            </StyledQuestion>
            <StyledAnswer>
              Valmeego will provide you with your own dashboard to record
              information relevant to you as an Ameego in due course.
              Information like trips invited to, trips you worked as an Ameego,
              number of recommendations provided, number of bookings made, value
              of bookings and commission and many other information points will
              be provided as we further develop the system.
            </StyledAnswer>
            <StyledQuestion>
              Can I, as an Ameego, also have my existing tourism business on
              Valmeego?
            </StyledQuestion>
            <StyledAnswer>
              Yes. If you have a business which is relevant to the Valmeego user
              base you can create a business profile for it. So, for example, if
              you had your own small tour guiding business you can create a
              business profile for it on Valmeego. We will show a link between
              your Ameego and Business profile so there may be possibility for
              cross over business.
            </StyledAnswer>

            <StyledFAQTitle>Business</StyledFAQTitle>

            <StyledQuestion>
              Can I take direct bookings on Valmeego?
            </StyledQuestion>
            <StyledAnswer>
              Absolutely, you can take direct bookings on Valmeego! In fact, we
              provide a highly personalized way for you to engage with your
              potential customers. Not only can you promote direct bookings, but
              you also can showcase any special offers by posting exclusive
              deals and promotions that catch the eye of locals and travellers
              seeking unique experiences. We understand the importance of
              streamlining your processes, which is why we allow you to
              integrate a direct link to your own booking system on your
              business profile. This ensures a smooth and efficient booking
              experience for your customers and direct communications between
              both parties.
            </StyledAnswer>
            <StyledQuestion>
              How do I get paid for direct bookings?
            </StyledQuestion>
            <StyledAnswer>
              Direct bookings are processed through your businesses booking
              engine and payment system. Valmeego has no input to this beyond
              providing the opportunity to the business and consumer to find
              each other.
            </StyledAnswer>
            <StyledQuestion>
              How do I get paid for Special Offers/ Deals offered on Valmeego?
            </StyledQuestion>
            <StyledAnswer>
              Businesses receive payment for their Special offer/deals bookings
              that have transpired on Valmeego through Stripes streamlined
              process that ensures convenience and security. When travellers
              make bookings and complete their transactions on Valmeego, the
              payment is processed through Stripe. Valmeego’s integrated system
              calculates the appropriate fees and charges. Once the booking is
              successfully completed, the funds are transferred directly to the
              business's linked Stripe account. This ensures a quick and
              efficient payment process, allowing businesses to access their
              earnings. Stripe is a secure and trusted payment processing system
              that enables seamless transactions.
            </StyledAnswer>
            <StyledQuestion>Is Valmeego Free for businesses?</StyledQuestion>
            <StyledAnswer>
              Valmeego offers all new businesses 15 months for the price of 12
              when they sign-up. This initial trial period allows businesses to
              experience Valmeego’s features and benefits including creating a
              business profile, listing products or services, engaging with
              potential customers, and understanding how Valmeego can benefit
              their business.
            </StyledAnswer>
            <StyledQuestion>
              Can my business deal with the customers directly?
            </StyledQuestion>
            <StyledAnswer>
              Yes. Businesses can communicate directly with customers. We are
              looking to drive repeat business and personal recommendations
              through the direct communication model facilitated through
              Valmeego.
            </StyledAnswer>
            <StyledQuestion>
              Can my business market future deals or offers to previous
              customers?
            </StyledQuestion>
            <StyledAnswer>
              Yes. Valmeego’s direct communication model allows you to have an
              ongoing promotional relationship with previous customers. Provide
              them with deals, special offers and other promotions directly to
              their Travel Feed. The opportunity now exists to turn all your
              customers into recommendation champions for your business - treat
              them right and they can directly recommend your business to their
              friends and circle of contacts. Valmeego is a great promotional
              tool to an already engaged audience.
            </StyledAnswer>
            <StyledQuestion>
              Can my business promote deals to my own locality?
            </StyledQuestion>
            <StyledAnswer>
              Yes. On Valmeego all users will receive deals, offers or special
              promotions into their travel feed from the local businesses. They
              can also specifically filter for those offers in any destination.
              Encouraging stronger business from locals is just as important as
              business from visitors. It can be a great tool for a business to
              extend their season well beyond the traditional timescales.
            </StyledAnswer>
            <StyledQuestion>
              Can I invite other businesses to Valmeego?
            </StyledQuestion>
            <StyledAnswer>
              Yes. If you know of other businesses in your local ecosystem that
              cater to the entertainment, hospitality, travel, and tourism
              sectors then send them an invite link with one click.
            </StyledAnswer>
            <StyledQuestion>
              Can I have several businesses on Valmeego?
            </StyledQuestion>
            <StyledAnswer>
              Yes, you can sign up multiple businesses on Valmeego. Each
              business will need its own account and key contact details.
            </StyledAnswer>
            <StyledQuestion>
              I signed up as a business, can I also have a Personal and Ameego
              account?
            </StyledQuestion>
            <StyledAnswer>
              Yes. As a member of Valmeego you can create a maximum of 3
              profiles - Personal, Business and Ameego. Many members will only
              ever have a personal account because they do not own a business or
              wish to be an Ameego. However, either or both of these additional
              profiles are available to any Valmeego member if they are
              applicable and they choose to add them.
            </StyledAnswer>

            <StyledFAQTitle>Destination Promoter</StyledFAQTitle>
            <StyledQuestion>
              Can my destination be contacted directly with visitor queries?
            </StyledQuestion>
            <StyledAnswer>
              Yes. Any user can search for and find your organisations profile
              from their Home page. They can then send their query directly
              through chat. This chat channel remains open for ongoing direct
              contact to take place. So, a potential visitor can be supported
              during their planning phase, while in destination and when they
              return home. Actionable feedback is now much easier to obtain.
            </StyledAnswer>

            <StyledQuestion>Is Valmeego free?</StyledQuestion>
            <StyledAnswer>
              Valmeego offers all new destination promoters 15 months for the
              price of 12 when they sign-up. This initial trial period allows
              destination promoters to experience Valmeego’s features and
              benefits including creating a profile, listing products or
              services, engaging with potential customers, and understanding how
              Valmeego can benefit their destination.
            </StyledAnswer>

            <StyledQuestion>
              Can we invite businesses in our destination to Valmeego?
            </StyledQuestion>
            <StyledAnswer>
              Yes, Destination promoters and local businesses are in partnership
              to bring economic benefit to their area. Valmeego encourages
              destinations to direct relevant businesses to Valmeego in order to
              promote what they offer and ensure the destination is more
              appealing to locals and visitors.
            </StyledAnswer>

            <StyledQuestion>
              Can we invite potential Ameegos in our area to Valmeego?
            </StyledQuestion>
            <StyledAnswer>
              Yes. Valmeego encourages destination promotors to invite suitable
              Ameego candidates to our growing ecosystem.
            </StyledAnswer>

            <StyledQuestion>
              Can we connect with other Destination Promoters on Valmeego?
            </StyledQuestion>
            <StyledAnswer>
              Yes. Valmeego is a global network of local travel ecosystems. Our
              platform will work best when local ecosystems in a region or
              country are interlinked. Visitors will come to an area if there is
              enough to see or do. That may require combining a number of local
              ecosystems into a stronger destination. Valmeego facilitates this.
            </StyledAnswer>
          </Col>
        </Row>
        <Row center="xs">
          <Spacer all={5}>
            <Heading bold={false} centered>
              Need more help? If your question is not answered here please{" "}
              <StyledLink onClick={() => setModalIsOpen(true)}>
                contact us
              </StyledLink>
              .
            </Heading>
          </Spacer>
        </Row>
      </PageSection>
    </>
  );
};

Faq.Layout = Public;
Faq.Title = "Frequently Asked Questions | Valmeego - Locally and beyond";

export default Faq;
